import productionSurfaceManifest from '../config/production-surface-manifest.json';

type ProductionSurfaceStatus =
  | 'production_supported'
  | 'pilot_supported'
  | 'internal_only'
  | 'hidden'
  | 'blocked';

type ProductionSurfaceRule = {
  pattern: string;
  status: ProductionSurfaceStatus;
  owner: string;
  module: string;
  reason: string;
};

export type ProductionSurfaceTenantContext = {
  tenantId?: string | null;
  isDemo?: boolean | null;
};

export const ACACIA_HOME_CARE_UAT_TENANT_ID = 'acac0000-0000-4000-a000-000000000001';

const visibleDashboardStatuses = new Set<ProductionSurfaceStatus>([
  'production_supported',
  'pilot_supported',
  'internal_only',
]);

const dashboardRules = (productionSurfaceManifest.dashboardRoutes as ProductionSurfaceRule[]).map(
  (rule, index) => ({
    ...rule,
    index,
    regex: patternToRegex(rule.pattern),
    specificity: surfacePatternSpecificity(rule.pattern),
  }),
);

export type DashboardProductionSurfaceMatch = ProductionSurfaceRule | null;

export function matchDashboardProductionSurface(pathname: string): DashboardProductionSurfaceMatch {
  const normalizedPathname = normalizeRoute(pathname);
  const match =
    dashboardRules
      .filter((rule) => rule.regex.test(normalizedPathname))
      .sort((a, b) => b.specificity - a.specificity || a.index - b.index)[0] ?? null;

  if (!match) return null;
  return {
    pattern: match.pattern,
    status: match.status,
    owner: match.owner,
    module: match.module,
    reason: match.reason,
  };
}

export function isDashboardProductionSurfaceVisible(pathname: string): boolean {
  const rule = matchDashboardProductionSurface(pathname);
  return !rule || visibleDashboardStatuses.has(rule.status);
}

export function isAcaciaHomeCareUatTenant(
  tenantContext: ProductionSurfaceTenantContext | null | undefined,
): boolean {
  return (
    tenantContext?.tenantId === ACACIA_HOME_CARE_UAT_TENANT_ID && tenantContext?.isDemo === true
  );
}

export function isDemoProductionSurfaceBypassTenant(
  tenantContext: ProductionSurfaceTenantContext | null | undefined,
): boolean {
  return tenantContext?.isDemo === true;
}

export function isDashboardProductionSurfaceVisibleForTenant(
  pathname: string,
  tenantContext?: ProductionSurfaceTenantContext | null,
): boolean {
  const rule = matchDashboardProductionSurface(pathname);
  if (!rule) return true;

  if (isDemoProductionSurfaceBypassTenant(tenantContext)) {
    if (rule.status === 'blocked' || rule.status === 'hidden') return true;
    return visibleDashboardStatuses.has(rule.status);
  }

  return visibleDashboardStatuses.has(rule.status);
}

function normalizeRoute(route: string): string {
  if (!route || route === '/') return '/';
  let normalized = route.replace(/\/+/g, '/');
  if (!normalized.startsWith('/')) normalized = `/${normalized}`;
  if (normalized.length > 1 && normalized.endsWith('/')) normalized = normalized.slice(0, -1);
  return normalized;
}

function patternToRegex(pattern: string): RegExp {
  const parts = normalizeRoute(pattern).split('/').filter(Boolean);
  const body =
    parts
      .map((part) => {
        if (part === '**') return '(?:/.*)?';
        if (part === '*') return '/[^/]+';
        return `/${escapeRegExp(part)}`;
      })
      .join('') || '/';
  return new RegExp(`^${body}$`);
}

function surfacePatternSpecificity(pattern: string): number {
  return normalizeRoute(pattern)
    .split('/')
    .filter(Boolean)
    .reduce((score, part) => {
      if (part === '**') return score;
      if (part === '*') return score + 1;
      return score + 4;
    }, 0);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
